import { supabase } from './supabase';

export type InvoicePaymentState = 'paid' | 'partial' | 'unpaid' | 'void';

export interface InvoiceLedgerTaxComponent {
  name: string;
  rate_bps: number;
  amount_cents: number;
  base_cents?: number;
}

export interface InvoiceLedgerEntry {
  id: string;
  business_id: string;
  booking_id: string | null;
  customer_id: string | null;
  invoice_number: string | null;
  status: string;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  tips_cents: number;
  surcharge_cents: number;
  tax_breakdown: InvoiceLedgerTaxComponent[];
  created_at: string;
  updated_at: string;
  due_at: string | null;
  finalized_at: string | null;
  payment_terms: string | null;
  is_overdue: boolean;
  booking_number: number | null;
  booking_status: string | null;
  booking_deleted: boolean | null;
  invoice_date: string | null;
  customer_name: string | null;
  customer_email: string | null;
  payment_state: InvoicePaymentState;
}

const SELECT = '*';

export type InvoiceLedgerMode = 'final' | 'drafts' | 'void' | 'all';

/**
 * Fetch invoices for the rebuilt Invoices tab.
 *  • 'final'  → invoices whose source booking is completed (issued invoices)
 *  • 'drafts' → invoices whose booking is NOT yet completed (in-progress/scheduled)
 *  • 'void'   → voided invoices only. Deleted bookings are INCLUDED here —
 *               deleting a booking is the main way an unpaid issued invoice
 *               gets voided, and the void must stay visible as a record.
 *  • 'all'    → everything
 * Deleted bookings are excluded from final/drafts.
 */
export async function fetchInvoicesLedger(
  businessId: string,
  opts: { mode?: InvoiceLedgerMode } = {},
): Promise<InvoiceLedgerEntry[]> {
  const mode = opts.mode ?? 'final';
  let query = supabase
    .from('invoices_ledger')
    .select(SELECT)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (mode === 'final') {
    // Voids are kept in the result so the UI can suppress the booking's row
    // instead of falling back to legacy booking totals; they render under the
    // separate Void toggle.
    query = query.eq('booking_status', 'completed').eq('booking_deleted', false);
  } else if (mode === 'drafts') {
    query = query.neq('booking_status', 'completed').eq('booking_deleted', false);
  } else if (mode === 'void') {
    query = query.eq('status', 'void');
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((row: any) => ({
    ...row,
    tax_breakdown: Array.isArray(row.tax_breakdown) ? row.tax_breakdown : [],
  })) as InvoiceLedgerEntry[];
}

export const PAYMENT_STATE_STYLES: Record<InvoicePaymentState, string> = {
  paid: 'bg-green-100 text-green-700',
  partial: 'bg-amber-100 text-amber-700',
  unpaid: 'bg-red-100 text-red-700',
  void: 'bg-gray-200 text-gray-500',
};

export const PAYMENT_STATE_LABELS: Record<InvoicePaymentState, string> = {
  paid: 'Paid',
  partial: 'Partial',
  unpaid: 'Unpaid',
  void: 'Void',
};

export const PAYMENT_TERMS_LABELS: Record<string, string> = {
  due_on_receipt: 'Due on receipt',
  net_7: 'Net 7',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_60: 'Net 60',
  custom: 'Custom',
};

export function getTermLabel(term: string | null | undefined): string {
  if (!term) return '';
  return PAYMENT_TERMS_LABELS[term] || term;
}
